import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

export const useVaccineRiskFactors = (
  patientId,
  snomedCode,
  vaccineType,
  enabled = true,
  doseNumber = null,
  vaccinationDate = null
) => {

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: [
      "vaccineRiskFactors",
      tenantVal,
      patientId,
      snomedCode,
      vaccineType,
      doseNumber,
      vaccinationDate,
    ],
    queryFn: async () => {
      // Never returns a risk-factor shape it did not receive. The response carries
      // `specVersion`, which is mandatory on save (SpecVersionRequiredValidator), and it
      // is on every response including the empty ones so that a vaccination with no
      // Indications is still saveable. Fabricating `{ defaultedRisks, selectableRisks }`
      // here dropped it, and the record then failed at save time complaining about a
      // field with no UI — while the form had every reason to believe the rules had
      // loaded (KIN-1807).
      if (!tenantVal || !patientId || !snomedCode) {
        throw new Error(
          "Cannot load vaccination rules without a tenant, patient and vaccine code."
        );
      }

      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/healthlink/vaccine-config/risk-factors`,
        {
          params: {
            patientId: patientId,
            vaccineSnomedCode: snomedCode,
            vaccineType: vaccineType,
            ...(doseNumber && { doseNumber }),
            ...(vaccinationDate && { vaccinationDate }),
          },
        }
      );

      if (!response?.data) {
        throw new Error("The vaccination rules came back empty.");
      }

      return response.data;
    },
    enabled: enabled && !!tenantVal && !!patientId && !!snomedCode && !!vaccinationDate,
    meta: { errorMessage: "Error fetching risk factors" },
  });
};
